import React, { useState, useEffect } from "react";

import { useFeaturedPosts } from "../../hooks/useFeaturedPosts";
import { SectionTitle } from "../common";
import FeaturedInitiative from "../initiatives/FeaturedInitiative";
import InitiativeCard from "../initiatives/InitiativeCard";
import { fetchAiLiteracyTutorBanner } from "../../services/aiLiteracyTutorService";
import { AiLiteracyTutorBanner } from "../../types";

const FALLBACK_IMAGE = "/images/initiatives/Picture1.png";

const buildImage = (url?: string | null) => url || FALLBACK_IMAGE;

const FeaturedInitiativesPlaceholder = () => (
  <section
    id="section-initiatives"
    className="home-section-placeholder home-section-placeholder--featured"
    aria-hidden="true"
  >
    <div className="container">
      <div className="home-section-placeholder__title" />
      <div className="featured-initiative">
        <div className="row align-items-center">
          <div className="col-lg-6">
            <div className="featured-initiative-left">
              <div className="featured-initiative-title-box">
                <div className="home-section-placeholder__line home-section-placeholder__line--short" />
                <div className="home-section-placeholder__title home-section-placeholder__title--left" />
              </div>
              <div className="home-section-placeholder__line home-section-placeholder__line--wide" />
              <div className="home-section-placeholder__line" />
              <div className="featured-initiative-btn-box">
                <div className="home-section-placeholder__button" />
              </div>
            </div>
          </div>
          <div className="col-lg-6">
            <div className="featured-initiative-right">
              <div className="featured-initiative-img-box">
                <div className="featured-initiative-img"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="initiatives-grid">
        <div className="initiative-card"></div>
        <div className="initiative-card"></div>
      </div>
    </div>
  </section>
);

const FeaturedInitiatives: React.FC = () => {
  const { featuredPosts, loading, error } = useFeaturedPosts();
  const [tutorBanner, setTutorBanner] = useState<AiLiteracyTutorBanner | null>(
    null,
  );

  useEffect(() => {
    fetchAiLiteracyTutorBanner()
      .then((data) => {
        setTutorBanner(data);
      })
      .catch((err) => {
        console.error(
          "Failed to load AI Literacy Tutor banner on home page:",
          err,
        );
      });
  }, []);

  const featuredFromApi = featuredPosts?.post
    ? {
        tagline: "🎓 Featured Initiative",
        title: featuredPosts.post.eng_title || "Featured Initiative",
        description: featuredPosts.post.eng_caption || "",
        image: buildImage(featuredPosts.post.eng_thumbnail),
        imageAlt: `${
          featuredPosts.post.eng_title || "Featured Initiative"
        } Image`,
        features: [],
        buttons: [
          {
            text: "Learn More",
            href:
              featuredPosts.post.eng_slug === "professor-e"
                ? "/professor-e"
                : `/initiatives/${featuredPosts.post.id}`,
            variant: "primary" as const,
          },
        ],
      }
    : null;

  const supportingFromApi = [
    featuredPosts?.supporting_post_1,
    featuredPosts?.supporting_post_2,
  ]
    .filter((item): item is NonNullable<typeof item> => Boolean(item))
    .map((item) => ({
      image: buildImage(item.eng_thumbnail),
      imageAlt: `${item.eng_title || "Initiative"} Image`,
      title: item.eng_title || "Initiative",
      description: item.eng_caption || "",
      buttons: [
        {
          text: "Learn More",
          href:
            item.eng_slug === "professor-e"
              ? "/professor-e"
              : `/initiatives/${item.id}`,
          variant: "primary" as const,
        },
      ],
    }));

  const hasSupporting = supportingFromApi.length > 0;

  if (loading) {
    return <FeaturedInitiativesPlaceholder />;
  }

  if (error || (!featuredFromApi && !tutorBanner)) {
    console.error("Failed to load featured initiatives:", error);
    return null;
  }

  console.log("tutorBanner", tutorBanner);

  return (
    <section id="section-initiatives">
      <div className="container">
        <SectionTitle
          subtitle="[ Our Initiatives ]"
          title="Featured Programs"
        />

        {featuredFromApi && (
          <FeaturedInitiative
            tagline={featuredFromApi.tagline}
            title={featuredFromApi.title}
            description={featuredFromApi.description}
            image={featuredFromApi.image}
            imageAlt={featuredFromApi.imageAlt}
            features={featuredFromApi.features}
            buttons={featuredFromApi.buttons}
          />
        )}

        {tutorBanner && (
          <div className="mt-5">
            <FeaturedInitiative
              tagline="🎓 Featured Initiative"
              title={tutorBanner.title || "AI Literacy Tutor"}
              description={tutorBanner.subtitle || ""}
              image={tutorBanner.image || FALLBACK_IMAGE}
              imageAlt={tutorBanner.title || "AI Literacy Tutor"}
              features={[]}
              buttons={[
                {
                  text: "Learn More",
                  href: "/ai-literacy-tutor",
                  variant: "primary" as const,
                },
              ]}
            />
          </div>
        )}

        {hasSupporting && (
          <div className="initiatives-grid mt-5">
            {supportingFromApi.map((card) => (
              <InitiativeCard
                key={card.title}
                image={card.image}
                imageAlt={card.imageAlt}
                title={card.title}
                description={card.description}
                buttons={card.buttons}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default FeaturedInitiatives;
